<script lang="ts">
	import { format } from 'svelte-i18n';
	import { CompoundChart, Loader } from '@Shelly/shelly-design-system';
	import { devices } from 'src/stores';
	import {
		energyMonitoringStats,
		type EnergyMonitoringStats,
	} from 'src/components/EnergyMonitoring/helpers/store';
	import type { Writable } from 'svelte/store';
	import {
		buildGenerationLegend,
		toDsChartData,
	} from 'src/components/EnergyMonitoring/helpers/layer_chart_adapter';
	import { getEnergyChartEmptyMessage } from '../helpers/energy_flow_helpers';
	import { getPVSetupDevices } from 'src/components/Consumption/PVSetupEnergy/pv_setup_helper';
	import { getActivePvSetupType } from '../helpers/pv_flow_helpers';
	import { ENERGY_PV_SETTINGS_HREF } from '../helpers/energy_widget_state_helpers';
	import { get } from 'svelte/store';
	import { createEnergyMonitoringStatsLoader } from '../helpers/energy_monitoring_stats_loader';
	import { energyMonitoringControls, energyMonitoringPeriodId } from '../helpers/store';
	import EmWidgetState from '../EmWidgetState.svelte';
	import EmWidgetShell from '../EmWidgetShell.svelte';
	import EmLayerChartFrame from '../EmLayerChartFrame.svelte';
	import EmChartLegend from '../EmChartLegend.svelte';

	export let statsStore: Writable<EnergyMonitoringStats> = energyMonitoringStats;
	export let requirePvSetup = false;
	export let embedded = false;

	const statsLoader = createEnergyMonitoringStatsLoader(statsStore);
	const refreshStats = () =>
		statsLoader.load(get(energyMonitoringControls), get(energyMonitoringPeriodId));

	$: loading = $statsStore.loading;
	$: sourceChartData = $statsStore.chartData;
	/** Match master Total Energy: stacked bar chart from overall consumption statistics. */
	$: normalizedChartData = toDsChartData(sourceChartData);
	$: chartData = normalizedChartData
		? { ...normalizedChartData, type: 'bar' as const, height: 220 }
		: null;
	$: hasPvProduction = Boolean(
		normalizedChartData?.series.some((series) => series.name === 'generated_energy'),
	);
	$: seriesMarks = {
		consumption: 'bar' as const,
		reversed: 'line' as const,
		total_house_energy: 'bar' as const,
		generated_energy: 'line' as const,
	};
	$: legendItems = buildGenerationLegend(normalizedChartData, (key, fallback) =>
		$format(key, { default: fallback }),
	);
	$: emptyMessage = getEnergyChartEmptyMessage($devices, (key, fallback) =>
		$format(key, { default: fallback }),
	);
	$: pvSetupDevices = getPVSetupDevices(getActivePvSetupType());
	$: hasCompletePvSetup =
		pvSetupDevices.gridDevices.length > 0 && pvSetupDevices.solarDevices.length > 0;
</script>

{#snippet generationContent()}
	{#if requirePvSetup && !hasCompletePvSetup}
		<EmWidgetState
			kind="setup"
			message={$format('energy_monitoring_pv_setup_missing', {
				default: 'Set up grid and solar meters to see PV generation.',
			})}
			minHeight="220px"
			actionHref={ENERGY_PV_SETTINGS_HREF}
			actionLabel={$format('energy_monitoring_pv_configure', { default: 'Set up PV' })}
		/>
	{:else if loading}
		<div class="d-flex justify-content-center align-items-center em-generation-loader">
			<Loader size="m" />
		</div>
	{:else if chartData && legendItems.length}
		<div class="em-generation-chart-block">
			<EmLayerChartFrame height={220} reserveLegendSlot={false}>
				{#key chartData.id ?? chartData.name}
					<CompoundChart
						{chartData}
						marks={seriesMarks}
						legend={false}
						showGradient={false}
						colorScheme={[
							'var(--color-chart-consumed-energy)',
							'var(--color-chart-generated-energy)',
						]}
					/>
				{/key}
			</EmLayerChartFrame>
			<EmChartLegend items={legendItems} />
		</div>
	{:else if $statsStore.error}
		<EmWidgetState
			kind="error"
			message={$format('energy_monitoring_load_error', {
				default: 'Energy data could not be loaded. Please try again.',
			})}
			minHeight="220px"
			onRefresh={refreshStats}
		/>
	{:else}
		<EmWidgetState message={emptyMessage} minHeight="220px" />
	{/if}
{/snippet}

{#if embedded}
	{@render generationContent()}
{:else}
	<EmWidgetShell
		widgetClass="energy-widget-chart"
		title={hasPvProduction || requirePvSetup
			? $format('energy_monitoring_generation_title', {
					default: 'Generation vs consumption',
				})
			: $format('energy_monitoring_returned_title', {
					default: 'Consumption vs returned energy',
				})}
		subtitle={$format('energy_monitoring_generation_subtitle', {
			default: 'Selected period balance',
		})}
		infoText={hasPvProduction || requirePvSetup
			? $format('energy_monitoring_generation_production_info', {
					default:
						'Historical chart of total home consumption and solar production for the selected period.',
				})
			: $format('energy_monitoring_returned_info', {
					default:
						'Historical chart of consumption and energy returned to the grid for the selected period.',
				})}
	>
		{@render generationContent()}
	</EmWidgetShell>
{/if}

<style>
	.em-generation-chart-block {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xxs);
		flex: 1 1 auto;
		min-width: 0;
		min-height: 220px;
	}

	.em-generation-loader {
		flex: 1 1 auto;
		min-height: 220px;
	}
</style>
